import { useNavigate } from 'react-router-dom'
import { CheckCircle, Edit3, ArrowRight } from 'lucide-react'
import { candidateDeepDive } from '@/features/candidates/data/mockData'

export const CandidateDeepDiveConfirmation = () => {
  const navigate = useNavigate()
  const { summary } = candidateDeepDive

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Review Your Career Summary</h1>
            <p className="text-gray-400 text-sm mt-1">Confirm your profile before we generate your match-ready summary</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/candidate/deep-dive/conversation')}
              className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors"
            >
              <Edit3 size={13} /> Edit Summary
            </button>
            <button
              onClick={() => navigate('/candidate/deep-dive/summary')}
              className="flex items-center gap-1.5 bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-4 py-2 rounded-lg hover:bg-[#ff8f45] transition-colors"
            >
              <CheckCircle size={13} /> Confirm & Save
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-8">

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Target roles */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Target Roles</p>
            <div className="space-y-2">
              {summary.targetRoles.map((role, i) => (
                <div key={role} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{i === 0 ? 'Primary' : 'Also open to'}</span>
                  <span className="px-3 py-1 bg-[#FD802E]/10 text-[#FD802E] rounded-full text-sm font-medium border border-[#FD802E]/20">
                    {role}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Location + Compensation */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
              <p className="text-xs uppercase text-gray-400 font-medium mb-3">📍 Location</p>
              <div className="space-y-1">
                {summary.locations.map((loc) => (
                  <p key={loc} className="text-white text-sm">{loc}</p>
                ))}
                <p className="text-gray-400 text-xs mt-1">Hybrid/Remote acceptable</p>
              </div>
            </div>
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
              <p className="text-xs uppercase text-gray-400 font-medium mb-3">💰 Compensation</p>
              <p className="text-white text-sm">{summary.compensation}</p>
            </div>
          </div>

          {/* Must-haves */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Must-Haves</p>
            <ul className="space-y-2">
              {summary.mustHaves.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm">
                  <CheckCircle size={14} className="text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Nice-to-haves */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Nice-to-Haves</p>
            <ul className="space-y-2">
              {summary.niceToHaves.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm">
                  <span className="w-3.5 h-3.5 rounded-full border border-gray-400 mt-0.5 shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 space-y-4">

          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle size={15} className="text-green-400" />
              <p className="text-white font-semibold text-sm">Profile Complete</p>
            </div>
            <p className="text-gray-400 text-xs">Your profile captures all key preferences</p>
          </div>

          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-2">
            <p className="text-xs uppercase text-gray-400 font-medium mb-1">Must-Haves</p>
            {summary.mustHaves.map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm">
                <CheckCircle size={12} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-gray-300 text-xs">{item}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-2">
            <p className="text-xs uppercase text-gray-400 font-medium mb-1">Nice-to-Haves</p>
            {summary.niceToHaves.map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm">
                <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                <span className="text-gray-300 text-xs">{item}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={() => navigate('/candidate/deep-dive/summary')}
              className="w-full flex items-center justify-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold py-3 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm"
            >
              Looks Good — Continue <ArrowRight size={14} />
            </button>
            <button
              onClick={() => navigate('/candidate/deep-dive/conversation')}
              className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors"
            >
              Go Back & Edit
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
